import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Checkbox,
  Autocomplete,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import React, { useState, useEffect, useRef } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AppLayout from '../components/AppLayout';
import { ObjectiveMeasureRatingCard } from '../components/ObjectiveMeasureRatingCard';
import { BehaviouralMeasureRatingCard } from '../components/BehaviouralMeasureRatingCard';
import { API_ENDPOINTS } from '../constants/api';
import { getLevelCriteria, normaliseRating } from '../utils/reviewRatingUtils';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSave, couldNotSubmit, reviewCycleCopy } from '../utils/userFacingCopy';
import { formatDateStandard, isUtcCalendarDatePassed } from '../utils/dateFormat';

interface ReviewObjectiveRating {
  id: string;
  objectiveMeasureId: string;
  objectiveMeasureName: string;
  level1Criteria?: string;
  level2Criteria?: string;
  level3Criteria?: string;
  employeeRating?: number | string;
  employeeComment?: string;
}

interface UserOption {
  id: string;
  fullName: string;
  email: string;
}

interface ObjectiveRatingInput {
  objectiveMeasureId: string;
  rating: number | null;
  comment: string;
}

interface ReviewBehaviouralRating {
  id: string;
  behaviouralMeasureId: string;
  behaviouralMeasureName: string;
  employeeRating?: number;
  employeeComment?: string;
}

interface BehaviouralRatingInput {
  behaviouralMeasureId: string;
  rating: number;
  comment: string;
}

interface Review {
  id: string;
  reviewCycleName?: string;
  /** Last day (inclusive) the employee may save or submit their self-review. */
  employeeCutoff?: string;
  employeeId: string;
  employeeName?: string;
  managerId?: string;
  status: string | number;
  objectiveRatings: ReviewObjectiveRating[];
  behaviouralRatings?: ReviewBehaviouralRating[];
  employeeDevelopmentAreas?: string;
  peerNominations?: { nominatedPeerId: string; nominatedPeerName: string }[];
  peerObjectiveMeasureIds?: string[];
  employeeHasIdpForCycleYear?: boolean;
}

export default function ReviewSelfReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [peerReviewSettings, setPeerReviewSettings] = useState<{ allowPeerReviews: boolean; minPeerReviewers: number; maxPeerReviewers: number }>({
    allowPeerReviews: false,
    minPeerReviewers: 2,
    maxPeerReviewers: 4,
  });

  const [objectiveRatings, setObjectiveRatings] = useState<ObjectiveRatingInput[]>([]);
  const [behaviouralRatings, setBehaviouralRatings] = useState<BehaviouralRatingInput[]>([]);
  const [developmentAreas, setDevelopmentAreas] = useState('');
  const [selectedPeers, setSelectedPeers] = useState<UserOption[]>([]);
  const [selectedPeerObjectiveIds, setSelectedPeerObjectiveIds] = useState<string[]>([]);
  const [peerPickerOptions, setPeerPickerOptions] = useState<UserOption[]>([]);
  const [peerPickerLoading, setPeerPickerLoading] = useState(false);
  const [errorIndices, setErrorIndices] = useState<number[] | null>(null);
  const [peerValidationError, setPeerValidationError] = useState<string | null>(null);
  const firstErrorRef = useRef<HTMLDivElement | null>(null);
  const submitErrorRef = useRef<HTMLDivElement | null>(null);
  const peerSectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Invalid review ID');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [reviewRes, peerRes] = await Promise.all([
          apiService.get<Review>(`/reviews/${id}`),
          apiService.get<{ allowPeerReviews: boolean; minPeerReviewers: number; maxPeerReviewers: number }>('/System/peer-review-settings').catch(() => ({ data: { allowPeerReviews: false, minPeerReviewers: 2, maxPeerReviewers: 4 } })),
        ]);
        if (cancelled) return;
        const rev = reviewRes.data;
        setReview(rev);
        setPeerReviewSettings(peerRes.data);

        const objRatings: ObjectiveRatingInput[] = rev.objectiveRatings.map((r) => {
          const raw = r.employeeRating;
          const isNa = raw === 'NotApplicable' || raw === 0;
          return {
            objectiveMeasureId: r.objectiveMeasureId,
            rating: isNa ? 0 : (normaliseRating(raw as number | string | undefined) ?? null),
            comment: r.employeeComment || '',
          };
        });
        const nominatedPeers: UserOption[] =
          peerRes.data.allowPeerReviews && rev.peerNominations?.length
            ? rev.peerNominations.map((pn) => ({ id: pn.nominatedPeerId, fullName: pn.nominatedPeerName || 'Unknown', email: '' }))
            : [];
        const peerObjectiveIds = rev.peerObjectiveMeasureIds ?? [];

        const behRatings: BehaviouralRatingInput[] = (rev.behaviouralRatings ?? []).map((br) => ({
          behaviouralMeasureId: br.behaviouralMeasureId,
          rating: normaliseRating(br.employeeRating as number | string | undefined) ?? 0,
          comment: br.employeeComment || '',
        }));

        setObjectiveRatings(objRatings);
        setBehaviouralRatings(behRatings);
        setDevelopmentAreas(rev.employeeDevelopmentAreas || '');
        setSelectedPeers(nominatedPeers);
        setSelectedPeerObjectiveIds(peerObjectiveIds);
      } catch (err: unknown) {
        if (cancelled) return;
        const axiosError = err as { response?: { status?: number } };
        if (axiosError.response?.status === 404) setError('Review not found');
        else if (axiosError.response?.status === 401) navigate('/login');
        else setError(couldNotLoad('the review'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!peerReviewSettings.allowPeerReviews || !currentUser?.id) return;
    let cancelled = false;
    setPeerPickerLoading(true);
    apiService
      .get<{ id: string; firstName: string; lastName: string; email: string; children?: unknown[] }[]>('/users/organogram?view=all')
      .then((res) => {
        if (cancelled) return;
        const flatten = (
          nodes: { id: string; firstName: string; lastName: string; email: string; children?: { id: string; firstName: string; lastName: string; email: string; children?: unknown[] }[] }[]
        ): UserOption[] => {
          let list: UserOption[] = [];
          for (const n of nodes) {
            list.push({ id: n.id, fullName: [n.firstName, n.lastName].filter(Boolean).join(' ') || 'Unknown', email: n.email ?? '' });
            if (n.children?.length) list = list.concat(flatten(n.children as { id: string; firstName: string; lastName: string; email: string; children?: unknown[] }[]));
          }
          return list;
        };
        const excluded = new Set([currentUser.id, review?.managerId].filter(Boolean));
        const options = flatten(res.data).filter((u) => !excluded.has(u.id));
        setPeerPickerOptions(options);
      })
      .catch(() => {
        if (!cancelled) setPeerPickerOptions([]);
      })
      .finally(() => {
        if (!cancelled) setPeerPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [peerReviewSettings.allowPeerReviews, currentUser?.id, review?.managerId]);

  useEffect(() => {
    if (errorIndices?.length && firstErrorRef.current) {
      const el = firstErrorRef.current;
      const t = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const trigger = el.querySelector('[role="button"]') as HTMLElement | null;
        if (trigger) trigger.focus();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [errorIndices]);

  // When peer validation error is set, scroll to the Peer review section (same pattern as unrated objectives)
  useEffect(() => {
    if (peerValidationError && peerSectionRef.current) {
      const t = setTimeout(() => {
        peerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [peerValidationError]);

  // When a validation/API error is shown at top, scroll it into view (e.g. behavioural, API errors)
  useEffect(() => {
    if (!submitError) return;
    let cancelled = false;
    const t = setTimeout(() => {
      const el = submitErrorRef.current;
      if (!cancelled && el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [submitError]);

  const handleSaveDraft = async () => {
    if (!review) return;
    if (isUtcCalendarDatePassed(review.employeeCutoff)) {
      setSubmitError(reviewCycleCopy.employeeCutoffPassed(formatDateStandard(review.employeeCutoff)));
      return;
    }

    setPeerValidationError(null);
    if (
      peerReviewSettings.allowPeerReviews &&
      selectedPeers.length > 0 &&
      objectiveRatings.length > 0 &&
      selectedPeerObjectiveIds.length === 0
    ) {
      setPeerValidationError('Please select at least one objective for your peers to rate.');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');
      await apiService.post(`/reviews/${review.id}/self-review/draft`, {
        objectiveRatings: objectiveRatings.map((r) => ({ objectiveMeasureId: r.objectiveMeasureId, rating: r.rating === 0 ? 'NotApplicable' : r.rating == null ? null : r.rating, comment: r.comment || undefined })),
        behaviouralRatings: behaviouralRatings.map((r) => ({ behaviouralMeasureId: r.behaviouralMeasureId, rating: r.rating || 0, comment: r.comment || undefined })),
        developmentAreas: developmentAreas || undefined,
        peerRaterNominationIds: selectedPeers.map((p) => p.id),
        peerObjectiveMeasureIds: selectedPeerObjectiveIds,
      });
      navigate(`/reviews/${id}`);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotSave('your draft')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!review) return;
    if (isUtcCalendarDatePassed(review.employeeCutoff)) {
      setSubmitError(reviewCycleCopy.employeeCutoffPassed(formatDateStandard(review.employeeCutoff)));
      return;
    }

    setPeerValidationError(null);
    if (peerReviewSettings.allowPeerReviews && selectedPeers.length > 0) {
      const n = selectedPeers.length;
      const { minPeerReviewers: min, maxPeerReviewers: max } = peerReviewSettings;
      if (n < min || n > max) {
        setPeerValidationError(`Please select between ${min} and ${max} peer reviewers, or remove all to skip peer review.`);
        return;
      }
      if (objectiveRatings.length > 0 && selectedPeerObjectiveIds.length === 0) {
        setPeerValidationError('Please select at least one objective for your peers to rate.');
        return;
      }
    }

    const unratedIndices = objectiveRatings
      .map((r, i) => ({ r, index: i }))
      .filter(({ r }) => r.rating == null)
      .map(({ index }) => index);
    if (unratedIndices.length > 0) {
      setErrorIndices(unratedIndices);
      return;
    }
    const naNoReasonIndices = objectiveRatings
      .map((r, i) => ({ r, index: i }))
      .filter(({ r }) => r.rating === 0 && !r.comment.trim())
      .map(({ index }) => index);
    if (naNoReasonIndices.length > 0) {
      setErrorIndices(naNoReasonIndices);
      setSubmitError('Please provide a reason (in the comment) for each objective marked N/A.');
      return;
    }
    if (behaviouralRatings.length > 0) {
      const unratedBeh = behaviouralRatings
        .map((r, i) => ({ rating: r.rating, index: i }))
        .filter(({ rating }) => !rating || rating < 1);
      if (unratedBeh.length > 0) {
        setSubmitError('Please rate all behavioural measures before submitting.');
        return;
      }
    }
    setErrorIndices(null);

    try {
      setSubmitting(true);
      setSubmitError('');
      await apiService.post(`/reviews/${review.id}/self-review/draft`, {
        objectiveRatings: objectiveRatings.map((r) => ({ objectiveMeasureId: r.objectiveMeasureId, rating: r.rating === 0 ? 'NotApplicable' : r.rating == null ? null : r.rating, comment: r.comment || undefined })),
        behaviouralRatings: behaviouralRatings.map((r) => ({ behaviouralMeasureId: r.behaviouralMeasureId, rating: r.rating || 0, comment: r.comment || undefined })),
        developmentAreas: developmentAreas || undefined,
        peerRaterNominationIds: selectedPeers.map((p) => p.id),
        peerObjectiveMeasureIds: selectedPeerObjectiveIds,
      });
      await apiService.post(API_ENDPOINTS.REVIEWS.SELF_REVIEW_SUBMIT(review.id));
      navigate(`/reviews/${id}`);
    } catch (err: unknown) {
            setSubmitError(getApiErrorMessage(err, couldNotSubmit('your review')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error || !review) {
    return (
      <AppLayout>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Review not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/reviews/${id}`)} sx={{ mt: 2, textTransform: 'none' }}>
          Back to review
        </Button>
      </AppLayout>
    );
  }

  const reviewStatus = String(review.status).toLowerCase();
  const isSelfReviewAlreadySubmitted = reviewStatus === 'selfreviewcomplete';
  const employeeCutoffPassed = isUtcCalendarDatePassed(review.employeeCutoff);

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, pb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate(`/reviews/${id}`)} sx={{ ml: -1 }} title="Back" aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <RateReviewIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0, flex: 1 }}>
            {isSelfReviewAlreadySubmitted ? 'Edit self-review' : 'Submit review'}
          </Typography>
        </Box>

        {employeeCutoffPassed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {reviewCycleCopy.employeeCutoffPassed(formatDateStandard(review.employeeCutoff))}
          </Alert>
        )}
        {!isSelfReviewAlreadySubmitted && !employeeCutoffPassed && review.employeeHasIdpForCycleYear === false && (
          <Alert severity="error" sx={{ mb: 2 }}>
            You must create an Individual Development Plan (IDP) for this year before you can submit your self-review. Go to My Development Plan to create one.
          </Alert>
        )}
        {submitError && (
          <Box ref={submitErrorRef} sx={{ scrollMarginTop: 24 }}>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          </Box>
        )}

        {objectiveRatings.length > 0 && (
          <>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 2 }}>
              Objective measures
            </Typography>
            {objectiveRatings.map((rating, index) => {
              const measure = review.objectiveRatings[index];
              return (
                <ObjectiveMeasureRatingCard
                  key={rating.objectiveMeasureId}
                  measureName={measure?.objectiveMeasureName ?? 'Objective measure'}
                  level1Criteria={getLevelCriteria(measure as Record<string, string | undefined>, 1)}
                  level2Criteria={getLevelCriteria(measure as Record<string, string | undefined>, 2)}
                  level3Criteria={getLevelCriteria(measure as Record<string, string | undefined>, 3)}
                  value={rating.rating}
                  onRatingChange={(val) =>
                    setObjectiveRatings((prev) => prev.map((r, i) => (i === index ? { ...r, rating: val } : r)))
                  }
                  comment={rating.comment ?? ''}
                  onCommentChange={(val) =>
                    setObjectiveRatings((prev) => prev.map((r, i) => (i === index ? { ...r, comment: val } : r)))
                  }
                  error={errorIndices !== null && errorIndices.includes(index)}
                  onClearError={() => {
                    setErrorIndices(null);
                    setSubmitError('');
                  }}
                  inputRef={errorIndices !== null && index === errorIndices[0] ? firstErrorRef : undefined}
                />
              );
            })}
          </>
        )}

        {review.behaviouralRatings && review.behaviouralRatings.length > 0 && (
          <>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: objectiveRatings.length > 0 ? 3 : 2 }}>
              Behavioural measures
            </Typography>
            {behaviouralRatings.map((rating, index) => {
              const measure = review.behaviouralRatings?.[index];
              return (
                <BehaviouralMeasureRatingCard
                  key={rating.behaviouralMeasureId}
                  measureName={measure?.behaviouralMeasureName ?? 'Behavioural measure'}
                  value={rating.rating}
                  onRatingChange={(val) =>
                    setBehaviouralRatings((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, rating: val } : r))
                    )
                  }
                  comment={rating.comment ?? ''}
                  onCommentChange={(val) =>
                    setBehaviouralRatings((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, comment: val } : r))
                    )
                  }
                  allowNa={false}
                />
              );
            })}
          </>
        )}

        <TextField
          fullWidth
          label="Development areas (optional)"
          value={developmentAreas}
          onChange={(e) => setDevelopmentAreas(e.target.value)}
          margin="normal"
          multiline
          rows={4}
          placeholder="Describe areas you want to focus on for development..."
        />

        {peerReviewSettings.allowPeerReviews && (
          <Box ref={peerSectionRef} sx={{ scrollMarginTop: 24 }}>
            {peerValidationError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPeerValidationError(null)}>
                {peerValidationError}
              </Alert>
            )}
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
              Peer review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {objectiveRatings.length > 0
                ? 'Nominate peers to rate your objectives. They are invited when you submit your self-review. Select which objectives they will review. This section is not mandatory to complete.'
                : 'This review has no objective measures, so peers cannot be assigned objective ratings. This section is not mandatory to complete.'}
            </Typography>
            <Autocomplete
              multiple
              options={peerPickerOptions}
              getOptionLabel={(opt) => opt.fullName || opt.email || opt.id}
              value={selectedPeers}
              onChange={(_, newValue) => {
                setSelectedPeers(newValue.slice(0, peerReviewSettings.maxPeerReviewers));
                setPeerValidationError(null);
              }}
              loading={peerPickerLoading}
              renderInput={(params) => (
                <TextField {...params} label="Peer reviewers" size="small" margin="dense" />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.fullName} {option.email ? `(${option.email})` : ''}
                </li>
              )}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              sx={{ mb: 2 }}
            />
            {selectedPeers.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected: {selectedPeers.map((p) => p.fullName).join(', ')} (min {peerReviewSettings.minPeerReviewers}, max {peerReviewSettings.maxPeerReviewers})
              </Typography>
            )}
            {objectiveRatings.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight="600" sx={{ mt: 2, mb: 1 }}>
                  Objectives for peer review (select at least one when you nominate peers) *
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 720 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }}>
                          <Checkbox
                            indeterminate={
                              selectedPeerObjectiveIds.length > 0 &&
                              selectedPeerObjectiveIds.length < objectiveRatings.length
                            }
                            checked={
                              objectiveRatings.length > 0 &&
                              objectiveRatings.every((r) => selectedPeerObjectiveIds.includes(r.objectiveMeasureId))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPeerObjectiveIds(objectiveRatings.map((r) => r.objectiveMeasureId));
                              } else {
                                setSelectedPeerObjectiveIds([]);
                              }
                              setPeerValidationError(null);
                            }}
                            aria-label="Select all objectives"
                          />
                        </TableCell>
                        <TableCell><strong>Objective</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {objectiveRatings.map((r, index) => {
                        const measure = review.objectiveRatings[index];
                        const checked = selectedPeerObjectiveIds.includes(r.objectiveMeasureId);
                        return (
                          <TableRow key={r.objectiveMeasureId} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={checked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPeerObjectiveIds((ids) => [...ids, r.objectiveMeasureId]);
                                  } else {
                                    setSelectedPeerObjectiveIds((ids) => ids.filter((id) => id !== r.objectiveMeasureId));
                                  }
                                  setPeerValidationError(null);
                                }}
                                aria-label={`Select ${measure?.objectiveMeasureName ?? r.objectiveMeasureId}`}
                              />
                            </TableCell>
                            <TableCell>{measure?.objectiveMeasureName ?? r.objectiveMeasureId}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
          {!employeeCutoffPassed && (
            <>
              <Button variant="outlined" onClick={handleSaveDraft} disabled={submitting} sx={{ textTransform: 'none' }}>
                {submitting ? <CircularProgress size={24} /> : isSelfReviewAlreadySubmitted ? 'Save changes' : 'Save draft'}
              </Button>
              {!isSelfReviewAlreadySubmitted && (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || review.employeeHasIdpForCycleYear === false}
                  sx={{ textTransform: 'none' }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Submit review'}
                </Button>
              )}
            </>
          )}
          {employeeCutoffPassed && (
            <Button variant="outlined" onClick={() => navigate(`/reviews/${id}`)} sx={{ textTransform: 'none' }}>
              Back to review
            </Button>
          )}
        </Box>
      </Box>
    </AppLayout>
  );
}
